import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chips } from "primereact/chips";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

import {
  isScopeComplete,
  scopeForApplicability,
} from "../helpers/ruleApplicability";
import { sanitizeDrgCodes } from "./InpatientRulesetForm";
import { RuleApplicabilityControl } from "./RuleApplicabilityControl";

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldHelp = styled.span`
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

export interface IUnitCapFormData {
  maxUnitsPerCode: number;
  /** CLE-2842 step 2 — Payer-Provider, Typical or Fallback. */
  applicability: RulesetApplicability;
  providerId: string | null;
  /** undefined = not chosen yet, null = All payers. */
  payerId: number | null | undefined;
  cptCodes: string[];
  groupName: string;
  contractLanguage: string;
}

export const INITIAL_UNIT_CAP_FORM_DATA: IUnitCapFormData = {
  maxUnitsPerCode: 1,
  applicability: "SCOPED",
  providerId: null,
  payerId: undefined,
  cptCodes: [],
  groupName: "",
  contractLanguage: "",
};

interface IUnitCapFormProps {
  title: string;
  submitLabel: string;
  formData: IUnitCapFormData;
  onChange: (data: IUnitCapFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /**
   * Edit mode: the applicability the rule was loaded with. A Typical rule stays Typical; a
   * Payer-Provider rule and a Fallback rule can be converted into each other (CLE-2842 step 2).
   * A Payer-Provider rule's provider and payer stay read-only on edit, as before, unless the
   * rule is being converted from a Fallback (then a provider has to be picked).
   */
  savedApplicability?: RulesetApplicability;
}

export function UnitCapForm({
  title,
  submitLabel,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  savedApplicability,
}: IUnitCapFormProps) {
  const navigate = useNavigate();

  const handleChange = <K extends keyof IUnitCapFormData>(
    field: K,
    value: IUnitCapFormData[K]
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const isValid =
    formData.cptCodes.length > 0 &&
    formData.maxUnitsPerCode >= 1 &&
    isScopeComplete(formData) &&
    !isSubmitting;

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
          aria-label="Back"
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Message
        severity="info"
        className="mb-4"
        content={
          <div>
            A <strong>Unit Cap</strong> rule reimburses each listed code at most{" "}
            <code>max units</code> times per service, regardless of how many
            units are billed (a payer&apos;s &quot;once per service&quot; list).
            Extra units are zeroed. This is different from a Package (which
            bundles <em>different</em> codes under a trigger) and a Case Rate
            (which keeps the single highest-priced code across a set).
          </div>
        }
      />

      <Card className="mb-4">
        <FieldGrid>
          <Field>
            <label htmlFor="maxUnitsPerCode">Max units per code</label>
            <InputNumber
              inputId="maxUnitsPerCode"
              value={formData.maxUnitsPerCode}
              onValueChange={(e) =>
                handleChange("maxUnitsPerCode", e.value ?? 1)
              }
              min={1}
              max={99}
              showButtons
            />
            <FieldHelp>
              How many units of each listed code the payer reimburses per
              service. 1 = &quot;once per service&quot; (the common case).
            </FieldHelp>
          </Field>
        </FieldGrid>

        <RuleApplicabilityControl
          applicability={formData.applicability}
          onApplicabilityChange={(next) =>
            onChange(scopeForApplicability(formData, next))
          }
          providerId={formData.providerId}
          onProviderChange={(providerId) =>
            handleChange("providerId", providerId)
          }
          payerId={formData.payerId}
          onPayerChange={(payerId) => handleChange("payerId", payerId)}
          // A saved Payer-Provider unit cap keeps its provider and payer read-only on edit, as
          // before. A conversion clears the provider, which unlocks both so one can be picked.
          scopedFieldsLocked={
            savedApplicability === "SCOPED" &&
            formData.applicability === "SCOPED" &&
            !!formData.providerId
          }
          savedApplicability={savedApplicability}
        />

        <FieldGrid>
          <Field>
            <label htmlFor="cptCodes">CPT / HCPCS Codes</label>
            <Chips
              inputId="cptCodes"
              value={formData.cptCodes}
              onChange={(e) =>
                handleChange("cptCodes", sanitizeDrgCodes(e.value ?? []))
              }
              placeholder="99213, 90471, G0121, 0272-0372"
              separator=","
            />
            <FieldHelp>
              Enter individual codes (99213, G0121) or ranges (10004-10012).
              Each code is capped independently.
            </FieldHelp>
          </Field>
        </FieldGrid>

        <FieldGrid>
          <Field>
            <label htmlFor="groupName">Description</label>
            <InputText
              id="groupName"
              value={formData.groupName}
              onChange={(e) => handleChange("groupName", e.target.value)}
              placeholder="Optional label (e.g. 'Aetna once-per-service list')"
            />
          </Field>
        </FieldGrid>

        <FieldGrid>
          <Field>
            <label htmlFor="contractLanguage">Contract Language</label>
            <InputTextarea
              id="contractLanguage"
              value={formData.contractLanguage}
              onChange={(e) => handleChange("contractLanguage", e.target.value)}
              rows={3}
              placeholder="Optional — the contract clause this rule models"
            />
          </Field>
        </FieldGrid>

        <Footer>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => navigate("/payer-rules")}
          />
          <Button
            label={submitLabel}
            icon="pi pi-check"
            onClick={onSubmit}
            disabled={!isValid}
            loading={isSubmitting}
          />
        </Footer>
      </Card>
    </div>
  );
}
